import { getCareSourceAttribution } from '../careSourceProcessing/careSourceAttribution';
import { getCareSourceOriginalText } from '../careSourceProcessing/careSourceStatusModel';

const INTERNAL_SOURCE_CONTEXTS = new Set([
  'document',
  'goal',
  'knowledge',
  'medication',
  'profile',
  'timeline',
]);

const TYPED_SOURCE_TYPES = new Set(['care_knowledge', 'typed_long_note', 'quick_check_in']);
const VOICE_SOURCE_TYPES = new Set(['dictated_text', 'voice']);
const IMAGE_SOURCE_TYPES = new Set(['care_knowledge_photo', 'care_knowledge_screenshot']);
const GENERIC_SOURCE_TITLES = new Set(['typed care info', 'care knowledge', 'care source', 'source']);
const SOURCE_TYPE_LABELS = Object.freeze({
  care_knowledge_document: 'Uploaded document',
  care_knowledge_photo: 'Photo',
  care_knowledge_screenshot: 'Screenshot',
  profile_document: 'Uploaded document',
  doctor_note: 'Clinical note',
  file_import: 'Imported file',
  ocr_text: 'Scanned document',
  email: 'Email',
  incoming_email: 'Email',
  forwarded_email: 'Email',
});

const cleanText = (value = '') => String(value || '').trim();

const getUsefulTitle = (sourceRef = {}, source = {}) => {
  const title = cleanText(
    source?.sourceMetadata?.fileName
    || source?.metadata?.source?.fileName
    || source?.fileName
    || sourceRef.sourceTitle
    || sourceRef.title
  );
  return title && !GENERIC_SOURCE_TITLES.has(title.toLowerCase()) ? title : '';
};

const formatFullDate = (value) => {
  if (!value) return '';
  const date = value?.toDate?.() || new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const getSourceDate = (sourceRef = {}, source = {}, fact = {}) => formatFullDate(
  source?.capturedAt
  || source?.createdAt
  || sourceRef.capturedAt
  || sourceRef.confirmedAt
  || sourceRef.createdAt
  || fact.capturedAt
  || fact.createdAt
);

const getSourceText = (sourceRef = {}, source = {}) => {
  const preservedText = cleanText(getCareSourceOriginalText(source));
  if (preservedText) return preservedText;

  const refText = cleanText(sourceRef.sourceText);
  if (refText) return refText;

  const context = cleanText(sourceRef.sourceContext);
  return INTERNAL_SOURCE_CONTEXTS.has(context.toLowerCase()) ? '' : context;
};

const getImageUrl = (source = {}) => {
  const attachments = [
    ...(Array.isArray(source.imageAttachments) ? source.imageAttachments : []),
    ...(Array.isArray(source.sourceMetadata?.imageAttachments) ? source.sourceMetadata.imageAttachments : []),
    ...(Array.isArray(source.metadata?.source?.imageAttachments) ? source.metadata.source.imageAttachments : []),
  ];
  const attachmentUrl = attachments
    .map((item) => cleanText(item?.url || item?.downloadUrl || item?.href))
    .find(Boolean);
  if (attachmentUrl) return attachmentUrl;

  return [
    ...(Array.isArray(source.mediaUrls) ? source.mediaUrls : []),
    ...(Array.isArray(source.sourceMetadata?.mediaUrls) ? source.sourceMetadata.mediaUrls : []),
  ].map(cleanText).find(Boolean) || '';
};

const getDocumentAttachment = (source = {}) => {
  const attachments = [
    ...(Array.isArray(source.documentAttachments) ? source.documentAttachments : []),
    ...(Array.isArray(source.sourceMetadata?.documentAttachments) ? source.sourceMetadata.documentAttachments : []),
    ...(Array.isArray(source.metadata?.source?.documentAttachments) ? source.metadata.source.documentAttachments : []),
  ];
  return attachments.find((item) => cleanText(item?.url || item?.downloadUrl || item?.href)) || null;
};

export const getProfileSourceId = (fact = {}) => {
  const refs = Array.isArray(fact.sourceRefs) ? fact.sourceRefs : [];
  return refs.find((ref) => ref?.sourceId)?.sourceId || '';
};

export const buildProfileSourceViewerModel = ({
  fact = {},
  sourceAction = {},
  source = null,
} = {}) => {
  const refs = Array.isArray(fact.sourceRefs) ? fact.sourceRefs : [];
  const sourceRef = refs.find((ref) => ref?.sourceId === source?.id)
    || refs.find((ref) => ref?.sourceText || ref?.sourceTitle || ref?.sourceType)
    || refs[0]
    || {};
  const sourceType = cleanText(
    sourceRef.sourceType
    || source?.sourceType
    || source?.sourceMetadata?.sourceType
    || source?.metadata?.source?.sourceType
  ).toLowerCase();
  const dateLabel = getSourceDate(sourceRef, source || {}, fact);
  const authorName = getCareSourceAttribution(source || sourceRef).authorName || 'Author not recorded';
  const sourceText = getSourceText(sourceRef, source || {});
  const imageUrl = getImageUrl(source || {});
  const documentAttachment = getDocumentAttachment(source || {});
  const documentUrl = cleanText(
    documentAttachment?.url || documentAttachment?.downloadUrl || documentAttachment?.href
  );
  const usefulTitle = getUsefulTitle(sourceRef, source || {});

  if (TYPED_SOURCE_TYPES.has(sourceType)) {
    return {
      title: 'Original entry',
      metadataLabel: [authorName, dateLabel].filter(Boolean).join(' · '),
      body: sourceText,
      imageUrl: '',
    };
  }

  if (VOICE_SOURCE_TYPES.has(sourceType)) {
    return {
      title: usefulTitle || 'Dictated note',
      metadataLabel: [authorName, dateLabel].filter(Boolean).join(' · '),
      body: sourceText,
      imageUrl: '',
    };
  }

  const isImage = IMAGE_SOURCE_TYPES.has(sourceType) || Boolean(imageUrl);
  const sourceTypeLabel = SOURCE_TYPE_LABELS[sourceType] || '';
  const fallbackTitle = isImage ? 'Original image' : sourceAction.sourceLabel || sourceTypeLabel || 'Original source';
  const originLabel = usefulTitle || sourceTypeLabel;
  return {
    title: usefulTitle || fallbackTitle,
    metadataLabel: [
      authorName,
      originLabel ? `From ${originLabel}` : '',
      dateLabel,
    ].filter(Boolean).join(' · '),
    body: sourceText,
    imageUrl,
    documentUrl,
    documentName: cleanText(
      documentAttachment?.fileName
      || documentAttachment?.filename
      || usefulTitle
    ),
  };
};
